// src/modules/app-views/omnichannel/pages/Init/modals/OmnichannelParametersModal/modals/ReconnectBaileysModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useRef, useState } from 'react';
import { Alert, Flex, Modal, Result, Spin } from 'antd';
import { CheckCircleOutlined, QrcodeOutlined, WhatsAppOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import omnichannelService from 'modules/app-views/omnichannel/service';

// *******************************************************************************
// HELPERS
// *******************************************************************************
const normalizePhone = p => String(p ?? '').replace(/\D/g, '').replace(/^0+/, '');

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ReconnectBaileysModal = ({ show, setShow, record, onSuccess, afterClose }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const intervalRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [qrCode, setQrCode] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [polling, setPolling] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [phoneRetries, setPhoneRetries] = useState(0);
  const [mismatchError, setMismatchError] = useState(null);
  const [phoneWarning, setPhoneWarning] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Phase 1: call reconnect to logout current session and generate a fresh QR
  useEffect(() => {
    if (!show || !record?._id) return;

    let active = true;

    setQrCode(null);
    setConnectionStatus('connecting');
    setPolling(false);
    setInitializing(true);

    omnichannelService
      .updateBaileysInstance(record._id, { reconnect: true })
      .then(() => {
        if (!active) return;
        setInitializing(false);
      })
      .catch(err => {
        if (!active) return;
        console.error('[ReconnectBaileysModal] reconnect error:', err);
        appService.message({ flag: 'e', text: 'error_saving' });
        setInitializing(false);
      });

    return () => {
      active = false;
    };
  }, [show, record?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Phase 2: once reconnect call completes, start polling for QR + connected status
  useEffect(() => {
    if (!show || !record?._id || initializing) return;

    let active = true;
    setPolling(true);

    const pollQr = async () => {
      try {
        const [data] = await omnichannelService.getBaileysQr(record._id);
        if (!active) return;

        if (data?.connectionStatus === 'connected') {
          const scanned = normalizePhone(data.connectedPhone);
          const typed = normalizePhone(record?.phoneNumber);

          if (!scanned) {
            setPhoneRetries(prev => prev + 1);
            if (phoneRetries + 1 >= 2) {
              setPhoneWarning(true);
              setPolling(false);
              clearInterval(intervalRef.current);
              setConnectionStatus('connected');
              setTimeout(() => onSuccess?.(), 0);
            }
            return;
          }

          if (typed && scanned !== typed) {
            setMismatchError({ typed, scanned });
            setConnectionStatus('connecting');
            setQrCode(null);
            setPolling(false);
            clearInterval(intervalRef.current);
            return;
          }

          setConnectionStatus('connected');
          setQrCode(null);
          setPolling(false);
          clearInterval(intervalRef.current);
          onSuccess?.();
          return;
        }

        if (data?.qrCode) {
          setPhoneRetries(0);
          setMismatchError(null);
          const qr = data.qrCode.startsWith('data:')
            ? data.qrCode
            : `data:image/png;base64,${data.qrCode}`;
          setQrCode(qr);
        }

        setConnectionStatus(data?.connectionStatus || 'connecting');
      } catch {
        // Silently ignore polling errors
      }
    };

    // Initial fetch
    pollQr();

    // Poll every 3 seconds
    intervalRef.current = setInterval(pollQr, 3000);

    // Stop polling after 2 minutes
    const timeout = setTimeout(() => {
      if (active) {
        setPolling(false);
        clearInterval(intervalRef.current);
      }
    }, 120000);

    return () => {
      active = false;
      clearInterval(intervalRef.current);
      clearTimeout(timeout);
    };
  }, [show, record?._id, initializing]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleClose = () => {
    setShow(false);
  };

  const handleAfterClose = () => {
    clearInterval(intervalRef.current);
    setQrCode(null);
    setConnectionStatus('connecting');
    setPolling(false);
    setInitializing(false);
    setPhoneRetries(0);
    setMismatchError(null);
    setPhoneWarning(false);
    afterClose?.();
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Modal
      open={show}
      onCancel={handleClose}
      afterClose={handleAfterClose}
      destroyOnHidden
      width={460}
      footer={null}
      mask={{ closable: connectionStatus !== 'connected' }}
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <WhatsAppOutlined style={{ color: '#25D366' }} />
          {translateX('reconnect_whatsapp')}
        </span>
      }
    >
      <Flex vertical align="center" gap={16} style={{ padding: '16px 0' }}>
        {connectionStatus === 'connected' ? (
          <Result
            icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            title={translateX('whatsapp_connected_success')}
            subTitle={translateX('whatsapp_reconnected_subtitle')}
          />
        ) : initializing ? (
          <Flex align="center" justify="center" style={{ width: 260, height: 200 }}>
            <Spin tip={translateX('reconnecting')} size="large">
              <div style={{ width: 260, height: 120 }} />
            </Spin>
          </Flex>
        ) : (
          <>
            <QrcodeOutlined style={{ fontSize: 24, color: '#25D366' }} />
            <span style={{ fontWeight: 500, fontSize: 14 }}>
              {translateX('scan_qr_code_instruction')}
            </span>

            {qrCode ? (
              <img
                src={qrCode}
                alt="QR Code"
                style={{ width: 260, height: 260, borderRadius: 8, border: '1px solid #e0e0e0' }}
              />
            ) : (
              <Flex align="center" justify="center" style={{ width: 260, height: 260 }}>
                <Spin tip={translateX('generating_qr_code')} size="large">
                  <div style={{ width: 260, height: 200 }} />
                </Spin>
              </Flex>
            )}

            {polling && (
              <span style={{ fontSize: 12, color: '#999' }}>
                {translateX('waiting_qr_scan')}
              </span>
            )}

            {!polling && connectionStatus !== 'connected' && !phoneWarning && (
              <span style={{ fontSize: 12, color: '#ff4d4f' }}>
                {translateX('qr_expired')}
              </span>
            )}

            {mismatchError && (
              <Alert
                type="error"
                showIcon
                message={`Número incorreto. Escaneie com o celular do número ${mismatchError.typed}.`}
                style={{ width: '100%' }}
              />
            )}

            {phoneWarning && (
              <Alert
                type="warning"
                showIcon
                message="Não foi possível verificar o número. Prosseguindo..."
                style={{ width: '100%' }}
              />
            )}
          </>
        )}
      </Flex>
    </Modal>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ReconnectBaileysModal;
